import { DateFormatter } from 'infrastructure/functions';

import {
  initialTemplate_step_2,
  initialTemplate_step_3,
  initialTemplate_step_4,
  type InitialSteppedValues,
} from '../templates/initial';

const isStepEmpty = (step: Record<string, unknown>) => {
  return Object.values(step).every(
    (i) => !i || (Array.isArray(i) && !i.length),
  );
};

export const generateInitialBlurb = (data: InitialSteppedValues) => {
  const { STEP1, STEP2, STEP3, STEP4, STEP5 } = data;
  let text = '';

  // -- STEP 1 --
  if (!isStepEmpty(STEP1)) {
    if (STEP1.confirmInitiatingVisit?.length) {
      if (STEP1.confirmInitiatingVisit.includes('visitCompleted')) {
        text += 'Initiating visit was completed';
        if (STEP1.visitDate) {
          text += ` on ${DateFormatter({ date: STEP1.visitDate })}`;
        }
        if (STEP1.visitType?.trim()) {
          text += ` as ${STEP1.visitType}`;
        }
        text += '. ';
      }

      if (STEP1.confirmInitiatingVisit.includes('establishedPatient')) {
        text += 'Patient is an established patient. ';
      }
    }
  }

  // -- STEP 2 --
  if (!isStepEmpty(STEP2)) {
    text += '\n';
    if (STEP2.recentHealthcareActivity?.length) {
      text += 'Patient had recent healthcare activity including ';
      const labels = initialTemplate_step_2[0].options
        .filter(
          (i) =>
            STEP2.recentHealthcareActivity?.includes(i.value) &&
            i.value !== 'other',
        )
        .map((i) => i.label.toString());
      if (
        STEP2.recentHealthcareActivity?.includes('other') &&
        STEP2.providerAppointmentOtherText?.trim()
      ) {
        labels.push(STEP2.providerAppointmentOtherText);
      }
      text += `${labels.join(', ')}`;
    }
    if (STEP2.dates?.trim()) {
      if (!STEP2.recentHealthcareActivity?.length) {
        text += 'Patient had recent healthcare activity';
      }
      text += ` on ${STEP2.dates}`;
    }
    text += '. ';
    if (STEP2.details?.trim()) {
      text += `Details include: ${STEP2.details}. `;
    }
  }

  // -- STEP 3 --
  if (!isStepEmpty(STEP3)) {
    if (STEP3.wasMedRecPerformed !== undefined) {
      text += '\n';
      text += 'MedRec ';
      if (STEP3.wasMedRecPerformed === 'yes') text += 'was ';
      if (STEP3.wasMedRecPerformed === 'no') text += 'was not ';
      text += 'performed. ';
    }

    if (STEP3.timing?.length) {
      text += 'MedRec ';
      if (STEP3.wasMedRecPerformed === 'yes') text += 'was ';
      if (STEP3.wasMedRecPerformed === 'no') text += 'was not ';
      text += 'performed during ';

      const optionWithText = initialTemplate_step_3[1].options[1];
      const values = STEP3.inAdvanceOfAppointmentOnText?.trim()
        ? STEP3.timing
        : STEP3.timing.filter((i) => i !== optionWithText.value);
      const labels = initialTemplate_step_3[1].options
        .filter((i) => values?.includes(i.value))
        .map((i) => i.label.replace('During ', ''));

      const timings = labels.map((label) => {
        if (label === optionWithText.label) {
          if (STEP3.inAdvanceOfAppointmentOnText?.trim()) {
            return `${optionWithText.label} ${STEP3.inAdvanceOfAppointmentOnText}`;
          }
          return '';
        }
        return label;
      });

      text += `${timings.join(', ')}. `;
    }

    if (STEP3.medicationDiscrepancies !== undefined) {
      text += '\n';
      text += 'Medication discrepancies ';
      if (STEP3.medicationDiscrepancies === 'yes') text += 'were ';
      if (STEP3.medicationDiscrepancies === 'none') text += 'were not ';
      text += 'identified. ';

      if (STEP3.medicationDiscrepanciesYesText?.trim()) {
        text += `${STEP3.medicationDiscrepanciesYesText}. `;
      }
    }

    if (STEP3.patientTakingMedicationsAsPrescribed !== undefined) {
      text += '\n';
      text += 'Patient ';
      if (STEP3.patientTakingMedicationsAsPrescribed === 'yes') text += 'is ';
      if (STEP3.patientTakingMedicationsAsPrescribed === 'no')
        text += 'is not ';
      text += 'taking medications as prescribed. ';

      if (STEP3.patientTakingMedicationsAsPrescribedNoText?.trim()) {
        text += `Reason: ${STEP3.patientTakingMedicationsAsPrescribedNoText}. `;
      }
    }
  }

  if (!isStepEmpty(STEP4)) {
    if (STEP4.condition1?.length) {
      text += '\n';
      text += 'Condition 1: ';
      const condition1Options1 = initialTemplate_step_4[0].items[0].options[0];
      const condition1Options2 = initialTemplate_step_4[0].items[0].options[1];
      const condition1Options3 = initialTemplate_step_4[0].items[0].options[2];

      if (STEP4.condition1.includes(condition1Options1.value)) {
        if (STEP4.condition1DiagnosisText?.trim()) {
          text += 'Diagnosis: ';
          text += `${STEP4.condition1DiagnosisText}. `;
        }
        if (STEP4.condition1DiagnosisICDText?.trim()) {
          if (STEP4.condition1DiagnosisText?.trim())
            text = text.slice(0, -2).concat(', '); // remove point symbol and add coma
          text += `(ICD: ${STEP4.condition1DiagnosisICDText}). `;
        }
      }

      if (STEP4.condition1.includes(condition1Options2.value)) {
        text += 'Current interventions: ';
        if (STEP4.condition1CurrentInterventionsText?.trim()) {
          text += `${STEP4.condition1CurrentInterventionsText}. `;
        }
      }

      if (STEP4.condition1.includes(condition1Options3.value)) {
        text += 'Measurable goal(s): ';
        if (STEP4.condition1MeasurableGoalText?.trim()) {
          text += `${STEP4.condition1MeasurableGoalText}. `;
        }
      }
    }

    if (STEP4.condition2?.length) {
      text += '\n';
      text += 'Condition 2: ';
      const condition2Options1 = initialTemplate_step_4[0].items[1].options[0];
      const condition2Options2 = initialTemplate_step_4[0].items[1].options[1];
      const condition2Options3 = initialTemplate_step_4[0].items[1].options[2];

      if (STEP4.condition2.includes(condition2Options1.value)) {
        if (STEP4.condition2DiagnosisText?.trim()) {
          text += 'Diagnosis: ';
          text += `${STEP4.condition2DiagnosisText}. `;
        }
        if (STEP4.condition2DiagnosisICDText?.trim()) {
          if (STEP4.condition2DiagnosisText?.trim())
            text = text.slice(0, -2).concat(', '); // remove point symbol and add coma
          text += `(ICD: ${STEP4.condition2DiagnosisICDText}). `;
        }
      }
      if (STEP4.condition2.includes(condition2Options2.value)) {
        if (STEP4.condition2CurrentInterventionsText?.trim()) {
          text += 'Current interventions: ';
          text += `${STEP4.condition2CurrentInterventionsText}. `;
        }
      }

      if (STEP4.condition2.includes(condition2Options3.value)) {
        if (STEP4.condition2MeasurableGoalText?.trim()) {
          text += 'Measurable goal(s): ';
          text += `${STEP4.condition2MeasurableGoalText}. `;
        }
      }
    }

    if (STEP4.individualizedCarePlanCreated !== undefined) {
      text += '\n';
      text += 'Individualized care plan ';
      if (STEP4.individualizedCarePlanCreated === 'yes') text += 'was ';
      if (STEP4.individualizedCarePlanCreated === 'no') text += 'was not ';
      text += ' created. ';

      const yesOptionsSubItems = initialTemplate_step_4[2].options[0].subItems;

      yesOptionsSubItems.forEach((item, idx) => {
        if (STEP4.interventionsList?.length) {
          const optionBlurbs: Record<string, string[]> = {};
          const interventionOptions = yesOptionsSubItems[idx].options.filter(
            (i) => i.value === 'interventions',
          );

          text += `\nInterventions implemented include:  `;

          interventionOptions.forEach((option) => {
            option.subItems?.forEach((subItem, index) => {
              if (STEP4[subItem.name]?.length) {
                const subItemOptions = option.subItems[index].options;
                subItemOptions
                  .filter((o) => STEP4[subItem.name]?.includes(o.value))
                  .forEach((o) => {
                    optionBlurbs[o.label] = [];
                  });

                subItemOptions.forEach((subOption) => {
                  subOption.subItems.forEach((subSubItem, i) => {
                    if (STEP4[subSubItem.name]?.length) {
                      const subSubItemOptions = subOption.subItems[i].options;
                      const opts = subSubItemOptions
                        .filter((o) =>
                          (STEP4[subSubItem.name] as string[])?.includes(
                            o.value,
                          ),
                        )
                        .map((o) => o.label);

                      optionBlurbs[subOption.label] = opts;
                    }
                  });
                });
              }
            });
          });

          text += `${Object.entries(optionBlurbs)
            .map(([key, value]) => {
              const options = value.length ? ` (${value.join(', ')})` : '';
              return `${key}${options}`;
            })
            .join(', ')}. `;

          const goalOptions = yesOptionsSubItems[idx].options.filter(
            (i) => i.value === 'goals',
          );
          goalOptions.forEach((option) => {
            option.subItems?.forEach((subItem, index) => {
              if (STEP4[subItem.name]?.length) {
                const subItemOptions = option.subItems[index].options;
                const opts = subItemOptions
                  .filter((o) => STEP4[subItem.name]?.includes(o.value))
                  .map((o) => o.label)
                  .join(', ');

                text += `\nGoals include:  ${opts}.`;
              }
            });
          });
        }
      });
    }

    if (STEP4.barriers?.length) {
      text += '\n';
      text += 'Barriers include: ';

      const barriers = initialTemplate_step_4[3].options
        .filter((i) => STEP4.barriers?.includes(i.value))
        .map((i) => i.label);

      if (barriers.includes('Other')) {
        const textWithOther = barriers.map((i) =>
          i.replace('Other', STEP4.barriersOtherText?.trim() ?? ''),
        );
        text += `${textWithOther.join(', ')}. `;
      } else {
        text += `${barriers.join(', ')}. `;
      }
    }

    if (STEP4.additionalBarrierNotes?.trim()) {
      text += `${STEP4.additionalBarrierNotes}. `;
    }

    if (STEP4.planIncludes?.length) {
      text += '\n';
      text += 'Plan includes: ';
      const planIncludes = initialTemplate_step_4[5].options
        .filter((i) => STEP4.planIncludes?.includes(i.value))
        .map((i) => i.label);
      text += `${planIncludes.join(', ')}. `;
    }
  }

  if (!isStepEmpty(STEP5)) {
    if (STEP5.phq2Completed !== undefined) {
      text += '\n';
      if (STEP5.phq2Completed === 'yes') text += 'PHQ-2 was completed. ';
      if (STEP5.phq2Completed === 'no') text += 'PHQ-2 was not completed. ';

      if (STEP5.phq2CompletedYesText?.trim()) {
        text += `PHQ-2 score: ${STEP5.phq2CompletedYesText}. `;
      }
      if (STEP5.phq2CompletedNoText?.trim()) {
        if (STEP5.phq2Completed === 'no') text = text.slice(0, -2).concat(' ');
        text += `due to ${STEP5.phq2CompletedNoText}. `;
      }
    }

    if (STEP5.sdohAssessmentCompleted !== undefined) {
      text += '\n';
      text += 'SDOH Assessment ';
      if (STEP5.sdohAssessmentCompleted === 'yes') text += 'was ';
      if (STEP5.sdohAssessmentCompleted === 'no') text += 'was not ';
      text += 'completed. ';
    }

    if (STEP5.anyUnmetNeeds !== undefined) {
      text += '\n';
      if (STEP5.anyUnmetNeeds === 'yes')
        text += 'Unmet SDOH needs were identified. ';
      if (STEP5.anyUnmetNeeds === 'no')
        text += 'Unmet SDOH needs were not identified. ';

      if (STEP5.anyUnmetNeedsYesText?.trim()) {
        if (STEP5.anyUnmetNeeds === 'yes')
          text = text.slice(0, -2).concat(', '); // remove point symbol and add coma

        text += `including: ${STEP5.anyUnmetNeedsYesText}. `;
      }
    }
  }

  return text;
};
